import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { MessageService } from "primeng/api";
import { catchError, map, mergeMap, of } from "rxjs";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { ReleaseActions } from "../actions/release.actions";

@Injectable()
export class ReleaseEffects {
  private messageService = inject(MessageService);

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) {}

  loadReleases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReleaseActions.loadRelease),
      mergeMap((action) =>
        this.restService.loadReleases(action.projectId).pipe(
          map((response) =>
            ReleaseActions.loadReleaseSuccess({ releaseList: response.data })
          ),
          catchError((error) =>
            of(ReleaseActions.loadReleaseFailure({ error: error.message }))
          )
        )
      )
    )
  );

  getReleaseById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReleaseActions.getReleaseById),
      mergeMap((action) =>
        this.restService.getReleaseById(action.projectId, action.releaseId).pipe(
          map((response) =>
            ReleaseActions.getReleaseByIdSuccess({ release: response.data })
          ),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.RELEASE_LOAD_FAILURE });
            return of(ReleaseActions.loadReleaseFailure({ error: error.message }));
          }
          )
        )
      )
    )
  );

  createRelease$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReleaseActions.createRelease),
      mergeMap((action) =>
        this.restService.createRelease(action.projectId, action.release).pipe(
          map((response) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.RELEASE_CREATE_SUCCESS });
            return ReleaseActions.createReleaseSuccess({
              release: response.data,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.RELEASE_CREATE_FAILURE });
            return of(
              ReleaseActions.createReleaseFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  updateRelease$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReleaseActions.updateRelease),
      mergeMap((action) =>
        this.restService.updateRelease(action.projectId, action.releaseId, action.release).pipe(
          map((response) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.RELEASE_UPDATE_SUCCESS });
            return ReleaseActions.updateReleaseSuccess({
              release: response.data,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.RELEASE_UPDATE_FAILURE });
            return of(
              ReleaseActions.updateReleaseFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  removeReleases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReleaseActions.removeRelease),
      mergeMap((action) =>
        this.restService
          .removeReleases(action.projectId, action.releaseIdList)
          .pipe(
            map((response) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.RELEASE_DELETE_SUCCESS });
              return ReleaseActions.removeReleaseSuccess({
                releaseIdList: response.data?.removed,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.RELEASE_DELETE_FAILURE });
              return of(
                ReleaseActions.removeReleaseFailure({ error: error.message })
              );
            })
          )
      )
    )
  );
}
